import {
    buildSchoolPublicOrigin,
    getSchoolSubdomainFromHost,
    isSchoolSubdomainsEnabled,
} from "../config/platformHost";

const RESERVED_SLUGS = new Set([
    "schools",
    "school",
    "login",
    "admin",
    "api",
    "choose",
    "register",
    "student",
    "teacher",
]);

export function slugifySchoolName(name) {
    return (
        String(name || "")
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/-+/g, "-")
            .replace(/^-+|-+$/g, "")
            .slice(0, 80) || "school"
    );
}

export function normalizePublicSlug(input) {
    return String(input || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9-]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 80);
}

export function isValidPublicSlug(slug) {
    if (!slug || slug.length < 3 || slug.length > 80) return false;
    if (RESERVED_SLUGS.has(slug)) return false;
    return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(slug);
}

export function publicSchoolPath(slug) {
    const safe = normalizePublicSlug(slug);
    return safe ? `/school/${encodeURIComponent(safe)}` : "/schools";
}

export function isOnSchoolSubdomain() {
    if (typeof window === "undefined") return false;
    return Boolean(getSchoolSubdomainFromHost(window.location.hostname));
}

/** Full public URL (subdomain in production) or origin + path on localhost. */
export function publicSchoolUrl(slugOrSchool) {
    const slug =
        typeof slugOrSchool === "string"
            ? normalizePublicSlug(slugOrSchool)
            : normalizePublicSlug(slugOrSchool?.publicSlug || slugOrSchool?.public_slug) ||
              (slugOrSchool?.schoolName ? slugifySchoolName(slugOrSchool.schoolName) : "");
    if (!slug) {
        const id = slugOrSchool?.schoolId || slugOrSchool?._id || slugOrSchool?.id;
        if (typeof window !== "undefined" && id) {
            return `${window.location.origin}${publicSchoolPath(id)}`;
        }
        return null;
    }
    if (isSchoolSubdomainsEnabled()) {
        const origin = buildSchoolPublicOrigin(slug);
        if (origin) return origin;
    }
    if (typeof window !== "undefined") {
        return `${window.location.origin}${publicSchoolPath(slug)}`;
    }
    return publicSchoolPath(slug);
}

/** Live / preview URL for School Page Builder. */
export function publicSchoolLiveUrl(slug, { preview = false } = {}) {
    const base = publicSchoolUrl(slug);
    if (!base) return "";
    if (!preview) return base;
    const sep = base.includes("?") ? "&" : "?";
    return `${base}${sep}preview=1`;
}

/** Prefer slug for public links; fall back to internal id (path routing on apex). */
export function publicSchoolHref(school) {
    const slug = school?.publicSlug || school?.public_slug;
    if (slug) return publicSchoolPath(slug);
    const id = school?.schoolId || school?._id || school?.id;
    return id ? `/school/${encodeURIComponent(id)}` : "/schools";
}

/** Navigate in-app on apex, or full navigation to school subdomain. */
export function goToPublicSchool(slugOrSchool, navigate) {
    const url = publicSchoolUrl(slugOrSchool);
    if (url && /^https?:\/\//i.test(url) && isSchoolSubdomainsEnabled() && !isOnSchoolSubdomain()) {
        window.location.assign(url);
        return;
    }
    navigate(publicSchoolHref(slugOrSchool));
}

export function looksLikeInternalSchoolId(value) {
    const v = String(value || "").trim();
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v)) {
        return true;
    }
    return /^c[a-z0-9]{20,}$/i.test(v);
}

export function publicSchoolLoginPath(slugOrSchool) {
    if (isOnSchoolSubdomain()) return "/login";
    const slug =
        typeof slugOrSchool === "string"
            ? slugOrSchool
            : slugOrSchool?.publicSlug || slugOrSchool?.schoolId || slugOrSchool?._id;
    if (!slug) return "/login";
    return `${publicSchoolPath(slug)}/login`;
}

export function publicSchoolApplyPath(slugOrSchool) {
    if (isOnSchoolSubdomain()) return "/apply";
    const slug =
        typeof slugOrSchool === "string"
            ? slugOrSchool
            : slugOrSchool?.publicSlug || slugOrSchool?.schoolId || slugOrSchool?._id;
    if (!slug) return "/schools";
    return `${publicSchoolPath(slug)}/apply`;
}
